/**
 * Everything the user reads, in one place.
 *
 * ADR-006 chose a typed string module over i18next, and the reason is that there is exactly one
 * locale. A translation library earns its cost when there are several: it brings locale negotiation,
 * plural rules, interpolation, lazy loading, and a runtime that can fail. Here it would be a
 * dependency and an indirection between a component and a sentence it renders, for a product that
 * ships `vi-VN` only.
 *
 * Typed rather than a plain object so a missing string is a compile error. The moment this file
 * becomes a `Record<string, string>`, a typo in a key renders nothing and no test notices.
 *
 * Vietnamese is the product language (spec P6), so these are the real strings, not placeholders. Where
 * a number or a name goes in, the function takes it as an argument rather than the caller
 * concatenating — word order differs between languages and interpolation is the thing that makes a
 * later second locale possible.
 */

import type { PlaceCategory } from "@trippool/contracts";

export const copy = {
  app: {
    name: "TripPool",
    /*
     * The wordmark's two halves, split because the second is tinted. They are copy rather than markup
     * because a translated locale may want a different split — or none — and a literal in the component
     * would make that impossible without editing the component.
     */
    wordmarkLead: "Trip",
    wordmarkAccent: "Pool",
    tagline: "Lưu nơi bạn muốn đi. Rồi để nó xếp lịch.",
  },

  /** Generic states, used across screens. */
  state: {
    loading: "Đang tải…",
    error: "Có lỗi xảy ra.",
    empty: "Chưa có gì ở đây.",
    /** The product's rule from AC-2.5: an unknown value is stated, never estimated. */
    unknown: "chưa xác định",
    retry: "Thử lại",
  },

  signIn: {
    heading: "Bắt đầu",
    intro: "Bạn có thể dùng thử ngay, không cần tài khoản. Mọi thứ đã lưu vẫn giữ nguyên khi bạn đăng ký sau.",
    asGuest: "Dùng thử không cần tài khoản",
    /*
     * What the button above actually does, said before it is pressed.
     *
     * Reported on 2026-09-28 as "everything I saved is gone": the database held all of it — five places, and
     * every other account's data untouched — and the browser was signed in as a fresh guest with an empty
     * list. A guest can only be created by tapping this one button, so nothing was deleted; what was
     * missing was any sentence saying that a trial account starts empty and that saved places belong to the
     * account they were saved under. The line above promises the trial's data survives a later sign-up,
     * which is true and is about the other direction.
     */
    guestEmpty:
      "Tài khoản dùng thử bắt đầu trống. Địa điểm đã lưu nằm trong tài khoản bạn đã dùng, hãy đăng nhập bằng email đó để thấy lại.",
    orEmail: "hoặc dùng email",
    email: "Email",
    password: "Mật khẩu",
    signIn: "Đăng nhập",
    register: "Tạo tài khoản",
    switchToRegister: "Chưa có tài khoản? Tạo mới",
    switchToSignIn: "Đã có tài khoản? Đăng nhập",
    displayName: "Tên hiển thị",
    guestNote: "Bạn đang dùng với tư cách khách.",
    claim: "Lưu tài khoản",
  },

  capture: {
    heading: "Thêm địa điểm",
    editHeading: "Sửa địa điểm",
    /** The one-line statement of what the form is for, under the heading. Create and edit differ. */
    subtitleCreate: "Lưu quán ăn, cafe, điểm tham quan vào danh sách du lịch",
    subtitleEdit: "Cập nhật thông tin chi tiết và toạ độ địa điểm",
    /** Distinct from `saved(name)`: this one confirms an *edit*, which saved nothing new. */
    savedEdit: "Đã lưu thay đổi.",
    /** Tier 1. The leading input accepts either a link or a name, and the help text says so. */
    primaryLabel: "Dán link hoặc gõ tên",
    primaryPlaceholder: "Dán link, hoặc gõ tên",
    primaryHelp: "Đủ để lưu. Mọi thứ khác có thể thêm sau, kể cả toạ độ, ở ô “Tìm địa điểm” bên dưới.",
    /**
     * Edit mode, where the leading field is prefilled with the place's name.
     *
     * The name moves to its own field the moment a link is pasted, so this says what the paste does
     * rather than leaving it a surprise — it is the only way to give a place a source it never had.
     */
    primaryHelpEdit: "Dán link bài gốc vào ô này để thêm nguồn; tên địa điểm vẫn được giữ ở ô bên dưới.",
    primaryHelpLink: "Đã nhận link. Đặt tên cho địa điểm này ở dưới.",
    nameLabel: "Đặt tên cho địa điểm này",
    nameRequired: "bắt buộc",
    namePlaceholder: "Cafe check-in cảnh đẹp",

    /** Tier 2, behind the disclosure. Nothing was removed from the form; only the moment changed. */
    details: "Thêm chi tiết",
    detailsHint: "khu vực, loại, địa chỉ, phù hợp…",
    detailsCollapse: "Thu gọn chi tiết",
    detailsExpand: "Thêm chi tiết",
    detailsSecondaryHint: "phù hợp, thời điểm, ghi chú…",
    changeLink: "Đổi link",
    /**
     * The source badge: which platform a pasted link came from, and what a link with nothing to show
     * prints instead.
     *
     * The platform names are the products they name — a translator keeps "TikTok" — but they are still
     * words the user reads, so they live here with the sentence around them and not in the function that
     * parses the URL. `link` is the fallback for a URL this form cannot even parse.
     */
    sourcePlatforms: {
      tiktok: "TikTok",
      instagram: "Instagram",
      facebook: "Facebook",
      googleMaps: "Google Maps",
      link: "Link",
    },
    /** A Google Maps URL whose path is empty: the badge still has to say where the link points. */
    sourceLocationFallback: "Vị trí đã lưu",
    /**
     * The credit line written onto the source row at capture.
     *
     * It is stored, not only rendered, which is why it is still one string in one place: the card and the
     * detail screen both read it back, and a copy written into the row at save time is the only way the
     * user's own attribution survives an edit.
     */
    sourceAttribution: "Xem bài gốc",
    mapHeading: "Bản đồ & Định vị",
    /** The hint under the pin map: what a tap does, and that a tap moves an existing pin. */
    mapHintSet: "Chạm hoặc click lên bản đồ để đặt ghim vị trí",
    mapHintChange: "Nhấp hoặc chạm trên bản đồ để đổi vị trí ghim",
    hasCoordinates: "Đã có toạ độ",
    sourceLabel: "Nguồn (URL)",
    sourcePlaceholder: "https://www.tiktok.com/@...",
    sourceHelp: "Điền sẵn nếu bạn dán link ở ô trên. Link chỉ được lưu lại, không được đọc.",
    /*
     * Shown when the form arrived with something already in it — text from the capture bar, or a place
     * picked from the map.
     *
     * It exists because the alternative is a form that looks empty when it is not: the leading field is
     * filled and the user's first instinct is to paste again, which is the reported defect this prefill
     * fixes. The line says the content is deliberate so the field is not mysterious.
     *
     * **It does not name the screen it came from, and it did.** The first version read "bạn vừa dán ở
     * Trang chủ" — true when Home was the only caller of the capture bar, and a plain lie the moment the
     * places list showed the same bar: measured in the browser, typing a name on `/places` and pressing
     * Lưu produced a form telling the user they had just pasted it on the home screen. This form cannot
     * know which of the two sent it, and it does not need to: what the user needs told is that the empty-
     * looking field is already filled.
     */
    prefilledFromText: "Đã điền sẵn nội dung bạn vừa nhập. Sửa lại nếu cần, rồi bấm Lưu.",
    prefilledFromMap: "Đã điền sẵn địa điểm bạn chọn trên bản đồ. Thêm gì cần rồi bấm Lưu.",

    areaLabel: "Khu vực",
    areaPlaceholder: "Gõ để tìm, hoặc Enter để tạo mới",
    areaHelp: "Gõ vài ký tự để tìm khu vực đã có, hoặc Enter để tạo mới. Một địa điểm có thể thuộc nhiều khu vực.",
    areaRemove: (name: string): string => `Bỏ ${name}`,
    /** The mark beside a typed area that does not exist yet, so the row says what tapping it does. */
    areaCreate: "Tạo",

    categoryLabel: "Loại",
    categoryMulti: "chọn được nhiều",
    subcategoryLabel: '"Khác" cụ thể là gì?',
    subcategoryRequired: "bắt buộc khi chọn Khác",
    subcategoryPlaceholder: "vd: vườn trái cây, tiệm bánh, sân bay",

    findLabel: "Tìm địa điểm hoặc địa chỉ",
    findPlaceholder: "Gõ tên quán, hoặc địa chỉ",
    findButton: "Tìm kiếm",
    findHelp: "Dán link Google Maps (hoặc toạ độ) để lấy vị trí ngay, hay gõ tên để tra địa điểm.",
    addressLabel: "Địa chỉ",
    addressPlaceholder: "Số nhà, đường, phường…",
    /** The two states of the address card: open it for editing, close it again. */
    addressEdit: "Sửa",
    addressDone: "Xong",

    /**
     * The provider's answer, with its confidence attached.
     *
     * Measured: for a venue absent from OpenStreetMap the provider answers with the city, kilometres
     * away. A wrong coordinate is invisible where a missing one is not, so the weak cases say so
     * rather than looking like the strong ones.
     */
    providerHeading: "Kết quả từ bản đồ",
    providerStrong: "Khớp",
    providerWeak: "Có thể không đúng, kiểm tra lại vị trí",
    providerUnusable: "Không tìm thấy địa điểm này. Hãy tự đặt ghim trên bản đồ.",
    providerUse: "Dùng vị trí này",
    providerUsed: "Đã dùng vị trí",
    providerUnavailable: "Không tra được bản đồ lúc này. Bạn vẫn đặt ghim tay được.",
    providerQuota: "Đã chạm giới hạn tra cứu. Bạn vẫn đặt ghim tay được.",
    providerNoResults: "Bản đồ không có kết quả nào cho tên này.",
    /**
     * Which index answered, named.
     *
     * Three providers now answer the same search — Google first, the Vietnamese index second and
     * OpenStreetMap when neither can — and they have different coverage, so a result that says where it
     * came from lets the user judge it. The names are products, so a translator keeps them; the fallback
     * is the app's own words for "a map", because an id this table does not know must still render as
     * something readable.
     */
    providerLabels: { google: "Google", goong: "Goong", nominatim: "OpenStreetMap", overpass: "OpenStreetMap" } as Record<string, string>,
    providerLabelFallback: "Bản đồ",
    /** The label in front of the provider name on a suggestion row. */
    providerSourceLabel: "Nguồn:",
    /**
     * The way out when the chain found nothing.
     *
     * A button rather than the two sentences above it, because they say what did not happen and this is
     * the one gesture that still works: no provider contains every place, and a tap on the map is exact.
     */
    providerPlaceOnMap: "Đặt ghim trên bản đồ",
    /** The heading over suggestions while the user types. */
    providerSuggestHeading: "Gợi ý địa điểm",

    aiSearchButton: "Tìm AI ✨",
    aiSearching: "Đang tìm AI...",
    aiAutoSearching: "✨ Bản đồ chưa có điểm này. Đang tự động tìm kiếm với Google AI...",
    aiApply: "Dùng kết quả này ✨",
    aiNotesHeading: "Gợi ý / Ghi chú AI",
    /** The heading over the AI candidate list. */
    aiHeading: "✨ Kết quả tìm kiếm từ Google AI",

    bestTimeLabel: "Thời điểm",
    bestTimeHelp: 'Chọn "cả ngày" nếu đi lúc nào cũng được. Khi đó các mục còn lại không cần thiết.',
    /*
     * The four slots, as labels. The `value` beside each is the contract's own slot id (`sáng`,
     * `chiều-tối`) and stays in the screen — it is sent to the server, not read by anybody — while the
     * `label` is what the chip prints, and one of them ("chiều/tối") is punctuated differently from its id.
     */
    bestTimeMorning: "sáng",
    bestTimeNoon: "trưa",
    bestTimeEvening: "chiều/tối",
    bestTimeAllDay: "cả ngày",

    /*
     * The suitability block. It was "Phù hợp" — the same words as the time-of-day block above it,
     * which is how two unrelated questions came to share a label and neither said what it meant.
     */
    suitabilityLabel: "Phù hợp với",
    suitabilityHelp: "Bỏ trống nghĩa là chưa rõ. Chọn để ghi rõ có hoặc không. Điều này quyết định địa điểm có vào được lịch trình phù hợp hay không.",
    suitabilityYes: "Có",
    suitabilityNo: "Không",
    /*
     * The one free-text field, and it is the place's own description.
     *
     * "Ghi chú" was ambiguous between two things the schema keeps apart, and the ambiguity was not
     * academic: the same text was written to `description` on edit and to a membership row's note on
     * create, so typing something and editing the place later made it visible, while saving it the first
     * time did not. One label, one storage location, and it is the place's own.
     */
    noteLabel: "Ghi chú về chỗ này",
    noteHelp: "Điều bạn muốn nhớ: món nên gọi, giờ đẹp nhất, chỗ đậu xe.",

    save: "Lưu",
    /** The button while the write is in flight. */
    saving: "Đang lưu...",
    /** The find button while the geocoder is being asked. */
    searching: "Đang tìm...",
    saved: (name: string): string => `Đã lưu ${name}`,
    cancel: "Huỷ",
    requiredName: "Cần một tên để lưu. Gõ tên, hoặc dán link rồi đặt tên.",
    recentTitle: "Đã lưu gần đây",

    /*
     * The marker beside a field the form filled from a source rather than from the user: a link's
     * coordinate, a provider's name/address/category, a nearby saved place's area.
     *
     * It exists because the alternative is a field that looks typed-in. The user then cannot tell which
     * values they are answerable for, and an edited guess is indistinguishable from the text they meant
     * to write. The mark is cleared the moment they type in the field.
     */
    derivedMark: "tự điền",
    /**
     * The pre-save duplicate warning. It says *may*, never *is*.
     *
     * Measured on the real corpus: two branches of one chain, same name, 149 m apart, are flagged — the
     * rule cannot see two venues behind a name and a distance. So the flag is evidence for the user to
     * judge, not proof, and the sentence must not tell them they have duplicated a place.
     */
    duplicateHeading: "Có thể bạn đã lưu chỗ này rồi",
    duplicateDetail: (name: string): string =>
      `“${name}” có tên gần giống và ở ngay đây. Nếu đúng là cùng một chỗ, gộp lại cho gọn.`,
    duplicateMerge: (name: string): string => `Gộp vào “${name}”`,
    /** The dismissal that is not a block. A chain with two nearby branches is two real places. */
    duplicateSaveAnyway: "Vẫn lưu thành địa điểm mới",
    duplicateDismiss: "Để tôi xem lại",
    /*
     * The third state: the rule could not run, and the screen says so rather than staying silent.
     *
     * Proximity is half the rule, so a draft with no coordinate cannot be checked at all — and silence
     * there was indistinguishable from "ran and found nothing", which is the one thing the notice must
     * not pretend. The sentence names the missing half and the way to supply it, and the action keeps
     * the save as frictionless as the duplicate case.
     */
    duplicateUncheckedHeading: "Chưa kiểm tra được trùng lặp",
    duplicateUncheckedDetail:
      "Địa điểm này chưa có toạ độ, mà quy tắc trùng lặp cần cả tên lẫn vị trí — nên chưa đối chiếu được với những chỗ đã lưu. Thêm ghim trên bản đồ nếu muốn kiểm tra.",
    duplicateUncheckedSave: "Vẫn lưu",
    mergedInto: (name: string): string => `Đã gộp vào “${name}”.`,
    /** Said when the fold failed after the place was already created, so nothing is silently lost. */
    mergeFailed: "Không gộp được, nhưng địa điểm đã được lưu.",
  },

  /**
   * The map picker's strings.
   *
   * The precision line is a sentence, not a bare number, because a coordinate error is invisible
   * and the user has no way to judge it except by being told. The above-threshold warning names
   * the measured consequence — the plan's order may change — rather than a vague "low accuracy",
   * which judges the map instead of the trip.
   */
  mapPicker: {
    label: "Vị trí",
    hint: "Bấm lên bản đồ để đặt ghim, hoặc kéo ghim để chỉnh.",
    noCoordinates: "Chưa có toạ độ. Bạn vẫn lưu được, nhưng địa điểm sẽ không vào được lộ trình.",
    clear: "Xoá ghim",
    precision: (metres: number): string => `chạm chính xác khoảng ±${metres} m`,
    precisionZoomIn: "phóng to để chính xác hơn",
    precisionAboveThreshold: "nên phóng to: sai hơn 600 m có thể làm đổi thứ tự lộ trình",
  },

  placeDetail: {
    /**
     * The back control's label, and the reason it says "Quay lại" rather than naming where it goes.
     *
     * It steps through the app's history: a place opened from Home comes back to Home, from the planner to
     * the planner, and only a page opened on its own lands on the places list. A label that promised the
     * list would be true for one of those paths and false for the rest.
     */
    backShort: "Quay lại",
    addToTrip: "Thêm vào lịch trình",
    savedPostSource: "Nguồn bài viết đã lưu",
    savedViaLink: "Lưu qua Link",
    savedManual: "Tạo thủ công",
    openOriginalLink: "Mở bài gốc ↗",
    openOriginalPost: (platform: string): string => `Mở bài gốc trên ${platform} ↗`,
    notesAndDetailsHeading: "Ghi chú & Chi tiết đã nhập",
    editNotesAction: "Chỉnh sửa ghi chú",
    /*
     * The note's empty state, and no privacy label above it.
     *
     * "Ghi chú riêng của bạn" ("your personal note") and "Chỉ bạn nhìn thấy" ("only you can see this"),
     * and the lock glyph that sat beside them, must not come back. The app has no login and every
     * visitor shares one account, so the note is readable by anyone; a promise of privacy the product
     * cannot keep is worse than no promise at all. The block carries no label either - its card is
     * already headed "Ghi chú & Chi tiết đã nhập", and a second, private-sounding one is the thing
     * this comment exists to prevent.
     */
    notesEmpty: "Chưa có ghi chú nào.",
    mapAndLocationHeading: "Bản đồ & Vị trí",
    specificAddress: "Địa chỉ cụ thể",
    /**
     * The map card's address slot when the place has no address recorded.
     *
     * The same words `home.noAddressYet` uses, kept as its own key because this screen is the one that
     * renders this slot and the two may drift apart (a row's second line and a card's address line are
     * different places). It was a literal at the call site until this change, which is the shape
     * `Home.tsx`'s header calls out as untranslatable.
     */
    noAddressYet: "Chưa có địa chỉ chi tiết",
    directionsAction: "Chỉ đường ↗",
    openGoogleMaps: "Mở Google Maps ↗",
    planningHeading: "Kế hoạch cho địa điểm này?",
    planningIntro: "Gắn địa điểm này vào các lịch trình sắp tới của bạn tại Đà Lạt.",
    addToTripButton: "Thêm vào lịch trình chuyến đi",
    suitabilityHeading: "Tiêu chí phù hợp",
    factsHeading: "Thông tin",
    /**
     * The absence of a coordinate is stated as a consequence, not as a gap.
     *
     * "chưa có toạ độ" tells the user a field is empty; that the place cannot enter a route tells them
     * what the emptiness costs them, which is the only reason they might go and fix it.
     */
    noCoordinatesConsequence: "Chưa có toạ độ, địa điểm này chưa vào được lộ trình.",
    indoor: "Trong nhà",
    indoorYes: "Trong nhà",
    indoorNo: "Ngoài trời",
    kids: "Trẻ em",
    elderly: "Người lớn tuổi",
    pets: "Thú nuôi",
    suitable: "Phù hợp",
    /** Distinct from "chưa xác định": a recorded no is an answer, and losing it loses information. */
    notSuitable: "Không phù hợp",
    duration: "Thời gian tham quan",
    price: "Mức giá",
    openingHours: "Giờ mở cửa",
    amenities: "Tiện ích",
    sources: "Nguồn",
    openSource: "Mở link gốc",
    /**
     * The map link, and why it names the provider.
     *
     * Every map in the app is already drawn from OpenStreetMap tiles, so this link lands on the same
     * map the card above shows — and naming it says where the click goes rather than the generic
     * "xem bản đồ", which leaves the destination to the user's guess. It is a plain hyperlink carrying
     * the coordinate, not an embedded map and not an API call.
     */
    openOnMap: "Mở trên OpenStreetMap",
    /**
     * The coordinate pill's accessible name. It repeats the digits the pill shows, so a screen reader
     * announces the same subject a sighted user reads instead of a bare link.
     */
    coordinatesOnMap: (lat: string, lon: string): string =>
      `Mở toạ độ ${lat}, ${lon} trên OpenStreetMap`,
    savedAt: (when: string): string => `Đã lưu lúc ${when}`,
  },


  /*
   * Turning a pasted Google Maps link — or a pasted coordinate pair — into a location.
   *
   * Its own block because **two screens do this now**, the capture form's find field and the map search,
   * and a message about the same event kept under one of its callers is how the second caller gets a
   * slightly different sentence for the same thing.
   *
   * Five, and each names a different fact. Two say where the coordinate came from, which matters because
   * the two paths cost the user different things: one was already in their clipboard, the other waited on a
   * round trip to Google. Three are failures that must not be collapsed —
   *
   * - `noCoordinate`: the link itself holds nothing. The remedy is to copy a link to the *place* rather
   *   than to a search.
   * - `unavailable`: Google could not be reached. Retry, or drag the pin.
   * - `notFound`: Google refused the link — a typo, an expired share. **Deliberately not "try again"**,
   *   because retrying a refused link can never work.
   *
   * Telling those three apart is the same distinction `provider_status` exists for on the API, and the
   * cost of merging them was measured: a dead link used to read as an outage and sent the user into a
   * retry loop.
   */
  mapLink: {
    fromLink: "Đã lấy toạ độ từ link bạn dán.",
    resolved: "Đã lấy toạ độ từ link Google Maps.",
    noCoordinate: "Link này không chứa toạ độ. Hãy mở đúng địa điểm trên Google Maps rồi sao chép link, hoặc gõ tên để tìm.",
    unavailable: "Không mở được link Google Maps lúc này. Bạn thử lại, hoặc tự đặt ghim trên bản đồ.",
    notFound: "Link Google Maps này không tồn tại (có thể đã hết hạn hoặc sai ký tự). Bạn kiểm tra lại link, hoặc tự đặt ghim trên bản đồ.",
    /** The heading over the place a pasted link turned out to be. */
    heading: "Từ link bạn dán",
  },

  /*
   * The quick-capture bar, which two screens now show.
   *
   * A block of its own rather than keys under `home`, because Home is no longer its only user: the places
   * screen shows the same control, and a control whose strings live under one of its callers is how the
   * second caller ends up with a slightly different sentence for the same thing.
   *
   * The three clipboard messages were literals inside `Home.tsx`'s paste handler — the one file this
   * module's own header says must not happen, since a string written at its call site is invisible to
   * anything that reads this file to find out what the user is told.
   */
  captureBar: {
    /** The field's accessible name as well as its placeholder: a placeholder is not a label. */
    label: "Thêm địa điểm mới",
    /*
     * Measured rather than chosen for its rhythm. At 390px the field is 165px wide and 17px type, so
     * `Dán link hoặc gõ tên địa điểm…` needed 238px — clipped on the reference phone by 73px, which is why
     * the screenshot read `Dán link hoặc gõ tên ` and stopped against the paste button. This needs 155px
     * and fits. `địa điểm` is the part that went: the section heading directly above already says what is
     * being added, and the accessible name below is unchanged.
     */
    placeholder: "Dán link hoặc gõ tên",
    /**
     * What the submit says.
     *
     * "Lưu" and not "Thêm", which was the other candidate: pressing it does not save anything yet — it
     * opens the form that does — and `Thêm` would promise a save this control cannot perform. The form on
     * the other side of the tap is where the word is honest, and it says "Lưu".
     */
    action: "Lưu",
    aiAction: "Tìm AI ✨",
    /**
     * The AI row's subtitle.
     *
     * The first version promised "vị trí & toạ độ chính xác", which is a claim the feature cannot keep:
     * a coordinate is taken only from a Google Maps link the model cites, and most answers cite none. It
     * says what is actually being asked for instead.
     */
    aiSuggestionMeta: "Tra tên và địa chỉ bằng Google AI",
    paste: "Dán",
    pasteTitle: "Dán nhanh từ clipboard",
    pasted: "Đã dán nội dung từ clipboard!",
    pasteEmpty: "Clipboard hiện đang trống.",
    /** The browser refused the read, which is the normal outcome on a page without the permission. */
    pasteDenied: "Vui lòng cấp quyền truy cập clipboard.",
    /** No `navigator.clipboard` at all: an old browser, or a page served over plain HTTP. */
    pasteUnsupported: "Trình duyệt không cho phép đọc clipboard tự động.",
    /**
     * The suggestion row's action.
     *
     * Not the capture form's "Dùng vị trí này": pressing this does not adopt a coordinate, it opens the
     * form that will — and the form is where a place is authored (ADR-004).
     */
    useSuggestion: "Chọn",
    /** The dropdown's accessible name. The rows beneath it are the suggestions. */
    suggestionsLabel: "Gợi ý địa điểm",
  },

  /*
   * The home screen answers one question: what next?
   *
   * Its greeting is split by time of day rather than being one string with a name, because "Chào buổi
   * sáng" and "Chào buổi tối" are not the same sentence with a variable in it — a greeting that reads
   * as machine-generated is the first thing a user sees.
   */
  home: {
    greetingMorning: (name: string): string => `Chào buổi sáng, ${name}`,
    greetingAfternoon: (name: string): string => `Chào buổi chiều, ${name}`,
    greetingEvening: (name: string): string => `Chào buổi tối, ${name}`,
    /**
     * The greeting's name when the account has none.
     *
     * A guest before they claim an account, and a password sign-up that skipped the display name. It is a
     * word a reader reads — "bạn" — so it belongs here with the sentences that wrap it, not as a default
     * argument in the screen.
     */
    greetingNameFallback: "bạn",
    /**
     * The hero's own heading.
     *
     * The screen had no statement of what it is for: a greeting, a field, and then a list. The intro
     * line is `copy.app.tagline`, which says what the product does and was written for exactly this —
     * reusing it rather than inventing a second one-liner is what keeps the promise consistent between
     * the sign-in screen and the first screen after it.
     */
    heroTitle: "Lưu nhanh một địa điểm",
    heroIntro: "Dán link TikTok, FB, Instagram hoặc gõ tên quán để lưu ngay.",
    /*
     * The two shortcuts under the hero: the other ways in, for a place with no link to paste.
     *
     * One key per chip and both under `home`, which is the only screen that renders them — the label is
     * the caller's, not the capture bar's, and it lived in `captureBar` for exactly as long as a second
     * screen rendered it.
     */
    quickMap: "Xem bản đồ",
    quickManual: "Thêm thủ công",
    /**
     * The row under the hero that says which links the bar takes.
     *
     * The platform names are the products they name, so they travel as a list: a translator keeps "TikTok"
     * as it is but still has the label over them and the order of the row in front of them, and a second
     * place the screen's words live is exactly what this module exists to prevent.
     */
    heroSourcesLabel: "Nguồn hỗ trợ:",
    heroSources: ["TikTok", "Instagram", "Facebook", "Google Maps"],
    /**
     * The stat bar, whose two numbers are counts of real rows: saved places, and the trips `useTrips`
     * returns. There is no companion slot because nothing models one — the version that printed a
     * hardcoded `2` there was a decoration, not a fact.
     */
    statsLabel: "Thống kê nhanh",
    statSaved: "Đã lưu",
    statTrips: "Lịch trình",
    /*
     * Names the area and the count, because the suggestion is only useful if the user can tell which
     * area it means. A generic "lên lộ trình" would be a suggestion about nothing.
     */
    tripTriggerTitle: (destination: string, placeCount: number): string =>
      `Lên lộ trình cho ${destination}?`,
    tripTriggerMeta: (placeCount: number): string =>
      `${placeCount} địa điểm đã lưu sẵn sàng để xếp lịch.`,
    tripTriggerDesc: "Chọn từ những nơi bạn đã lưu, rồi để nó xếp lịch.",
    tripTriggerAction: "Bắt đầu",
    /** The card's own badge and its decline path, both read as words rather than written in the screen. */
    tripTriggerBadge: "Gợi ý chuyến đi",
    tripTriggerDismiss: "Để sau",
    recentSavesTitle: "Đã lưu gần đây",
    recentSavesSync: "Tự động đồng bộ vị trí",
    /**
     * The link to the full list, with the count in it.
     *
     * One string rather than a label glued to a number at the call site, for the same reason
     * `trips.tripCount` is one: the count and the thing it counts are one phrase, and a language that
     * orders or punctuates them differently cannot be served by two pieces.
     */
    viewAllPlacesCount: (count: number): string => `Xem tất cả (${count})`,
    /**
     * The area filter, and the count is inside both strings.
     *
     * The chips are tabs — `role="tab"` and `aria-selected`, matching the trips screen — and the all-case
     * needs its own number, which the generic per-area form cannot carry because it takes a name.
     */
    filtersLabel: "Lọc theo khu vực",
    filterAllCount: (count: number): string => `Tất cả (${count})`,
    filterArea: (name: string, count: number): string => `${name} (${count})`,
    /** A saved place with no address yet, on the row's second line. */
    noAddressYet: "Chưa có địa chỉ chi tiết",
    /**
     * The two states a saved place can be in for planning.
     *
     * The located state is shown here as well as on the card because Home is where a user decides what to
     * plan with, and a place with no coordinate cannot enter a route — the word is the flag, not a colour.
     */
    badgeNoLocation: "Thiếu toạ độ",
    badgeLocated: "Đã định vị",
    /** The map banner: its heading, its promise, its button, and the pin count over the preview. */
    mapBannerLabel: "Bản đồ cụm địa điểm",
    mapBannerTitle: (count: number): string => `Xem ${count} địa điểm trên bản đồ tương tác`,
    mapBannerDesc: "Các điểm lưu của bạn được tự động gom cụm theo khoảng cách thực tế, giúp tối ưu lộ trình di chuyển.",
    mapBannerAction: "Mở bản đồ toàn cảnh",
    mapBannerPinsAt: (count: number, area: string): string => `${count} ghim tại ${area}`,
    mapBannerPins: (count: number): string => `${count} ghim`,
  },

  /** EPIC 4's entry point. The planner itself is not built, so this screen is a list and a promise. */
  trips: {
    heading: "Chuyến đi",
    /*
     * What this screen is for, stated on it.
     *
     * Reported confusion: the tab exists to plan a route *from saved places* (spec §1, productLoop steps
     * 3–5), and nothing on it said so — so an empty Trips tab beside an empty places list looked like two
     * unrelated dead ends.
     */
    purpose: "Lộ trình được xếp từ những địa điểm bạn đã lưu.",
    /*
     * The count that rides beside the heading.
     *
     * The number and the noun it counts are one string rather than a number concatenated with a label in the
     * markup: a count and its classifier agree grammatically, and a language whose classifier changes with the
     * number cannot be served by two pieces glued together at the call site.
     */
    tripCount: (count: number): string => `${String(count)} chuyến`,
    needsPlaces: "Bạn chưa lưu địa điểm nào. Lưu vài chỗ trước rồi quay lại đây để xếp lịch.",
    needsLocatedPlaces: "Địa điểm bạn đã lưu chưa có toạ độ, nên chưa xếp lộ trình được. Thêm toạ độ cho ít nhất 2 chỗ.",
    savePlaces: "Lưu địa điểm",
    empty: "Chưa có chuyến đi nào.",
    newTrip: "Lên lộ trình mới",
    /*
     * The list's filters.
     *
     * Named by when the trip is, not by what has been done to it: "hoàn thành" is the status `completed`,
     * which the server sets and which means the trip is finished with — a different fact from its dates
     * having passed. A tab called "Đã hoàn thành" over a date filter would put two meanings on one word, so
     * this one says "Đã qua".
     *
     * The count is part of the string rather than a number rendered beside it, the same shape
     * `expenses.filterAll` uses for the fund's filters: "Tất cả (5)" is one phrase a translator can order,
     * where a label and a separate count element are two pieces glued together at the call site.
     */
    filterAll: (count: number): string => `Tất cả (${String(count)})`,
    filterUpcoming: (count: number): string => `Sắp diễn ra (${String(count)})`,
    filterPast: (count: number): string => `Đã qua (${String(count)})`,
    /** A filter that matched nothing is not an empty account, and this says which it is. */
    filterEmpty: "Không có chuyến nào trong mục này.",
    /*
     * The state a trip card reports, one label per value the contract defines.
     *
     * Every one of the four: a switch that silently drops `completed` and `archived` would render them as
     * whatever the fallback was, and the contract's own reason for splitting `draft` from `planned` is that
     * the two are different facts to a reader.
     */
    statusDraft: "Đang lên lịch",
    statusPlanned: "Đã có lộ trình",
    statusCompleted: "Đã đi",
    statusArchived: "Đã lưu trữ",
    /** One day count, one place count, one party, one date range: the facts a card carries. */
    daysNights: (days: number, nights: number): string => `${String(days)} ngày ${String(nights)} đêm`,
    placeCount: (count: number): string => `${String(count)} địa điểm`,
    partySize: (adults: number, children: number): string =>
      children === 0 ? `${String(adults)} người lớn` : `${String(adults)} người lớn · ${String(children)} trẻ em`,
    /** "03/10 - 05/10": the range a card carries, both dates formatted by the screen that knows the year. */
    dateRange: (from: string, to: string): string => `${from} - ${to}`,
    /**
     * The month under a card's day number: "TH10".
     *
     * The app writes one date format rather than a locale's — the same rule `tripDates.ts` states — so the
     * abbreviation lives here beside the range's format and not in the screen that draws the tile.
     */
    monthShort: (month: number): string => `TH${String(month)}`,
    /** A start date the contract's format cannot read: a mark, because a blank tile reads as a broken card. */
    noDate: "-",
    /** The menu on a card. `moreActions` is its accessible name; a three-dot glyph says nothing aloud. */
    moreActions: "Thao tác khác",
    renameAction: "Đổi tên",
    renameHeading: "Đổi tên chuyến đi",
    renameLabel: "Tên chuyến đi",
    renameSave: "Lưu tên",
    duplicateAction: "Nhân bản chuyến đi",
    /** A copy keeps the destination, the dates, the places and the arrangement, and starts without plans. */
    duplicateNote: "Bản sao giữ nguyên điểm đến, ngày, địa điểm và cách xếp ngày. Phương án lịch trình thì không.",
    duplicateSuffix: "bản sao",
    continueEditing: "Tiếp tục chỉnh sửa",
    /*
     * Nothing saved yet, said as an invitation rather than as a status.
     *
     * The screen distinguishes this from "you cannot plan": one is fixed by making a trip, the other by
     * saving places, and they are two blocks with two buttons for that reason.
     */
    emptyTitle: "Chưa có kế hoạch nào",
    emptyBody: "Lên lộ trình đầu tiên từ những địa điểm bạn đã lưu.",
    /** Reading a saved trip back: the itinerary lives on its own page, not in the planner's state. */
    openTrip: "Mở chuyến đi",
    draft: "Chưa chọn phương án",
    planned: "Đã chọn lộ trình",
    noPlansYet: "Chuyến này chưa có phương án nào. Tạo ba phương án để chọn lộ trình.",
    generatePlans: "Tạo 3 phương án lịch trình",
    tripNotFound: "Không tìm thấy chuyến đi này. Có thể nó đã bị xoá.",
    /** Managing a saved trip. `edit` opens the planner on it; `remove` is the confirm's own verb. */
    edit: "Sửa",
    remove: "Xoá",
    confirmRemove: (title: string): string => `Xoá chuyến "${title}"?`,
    removeWarns:
      "Lịch trình, các phương án và chỗ ở của chuyến này sẽ không còn. Những địa điểm bạn đã lưu vẫn giữ nguyên.",
    editHeading: "Sửa chuyến đi",
    editingNote: "Đang sửa chuyến đã lưu, lưu lại sẽ cập nhật chính chuyến này.",
    /**
     * The plan summary's own name, above its figures.
     *
     * It was written inline with a party emoji in `PlanRoadmap`; both are copy, and the emoji is not how
     * this app draws a state. The name is the plan's own stored label (`Thư giãn`, `Cân bằng`, `Khám
     * phá`).
     *
     * The trip screen has no control for choosing among variants. The segmented mode switch went first,
     * measured: with an arrangement the three strategies carried identical stops, order, distance and
     * day split, differing only in rest hours, and a switch between identical itineraries is not a
     * switch. The "Chọn phương án này" button that recorded the shown plan followed it for the same
     * reason — a choice between three plans that are the same plan — so the trip screen reads the plan
     * it is given and commits to nothing; the planner's review step is where a plan is confirmed.
     */
    chosenPlan: (label: string): string => `Lịch trình đã chọn: ${label}`,
  },

  /**
   * The trip fund, as a tab inside the trip screen.
   *
   * Every string the fund shows lives here. A side with nothing recorded is a genuine zero — the sum of
   * an empty ledger is `0`, so the balance, the three cells and the wallet chip all read `0 đ` in the
   * money slots rather than words, which break the numeric structure and run long. The fact that *nothing
   * has been recorded yet* is a different statement from "the total is zero", and it belongs to the empty
   * state's own sentence (`emptyHint`) and the action under it, not to every money slot.
   *
   * Amounts are interpolated as already-formatted strings (`formatVnd`), not as numbers: the grouping
   * and the `đ` belong to the money formatter, and a count and its classifier belong to a language.
   */
  expenses: {
    tabItinerary: "Lộ trình",
    tabFund: "Quỹ",
    /** The chip in the trip header. It is a button that opens the tab; the label says the amount. */
    chipLabel: "Quỹ",
    chipOpen: (amount: string): string => `Xem quỹ chuyến đi, ${amount}`,
    chipOpenNegative: (amount: string): string => `Xem quỹ chuyến đi, âm ${amount}`,
    potLabel: "Quỹ còn lại",
    contributed: "Đã góp",
    spent: "Đã chi",
    entryCount: "Số khoản",
    /** The money slot's own zero: an empty ledger's sum, in every state, in the numeric slots. */
    zero: "0 đ",
    /** Money in, beside the pot: positive, negative or empty, with no threshold. */
    topUp: "+ Thu quỹ",
    /** The other verb, beside the top-up: a row out of the pot, recorded on the same sheet. */
    recordExpense: "- Ghi khoản chi",
    emptyList: "Chưa ghi khoản nào",
    /** The empty state's own sentence, carrying the fact the money slots no longer spell out. */
    emptyHint: "Quỹ chưa ghi gì: chưa ai góp, chưa chi gì.",
    /** The one action the empty state offers, so the record control is not hunted for. */
    emptyAction: "+ Ghi nhận khoản đầu tiên",
    negativeLine: "Quỹ đang âm: đã chi vượt số đã góp",
    kindContribution: "Góp",
    kindExpense: "Chi",
    potAfter: (amount: string): string => `Quỹ ${amount}`,
    dayLabel: (day: number): string => `Ngày ${String(day)}`,
    dayNone: "Chưa gắn ngày",
    /** The day head's remaining pot, so a folded day still reads its running figure. */
    dayRemaining: (amount: string): string => `còn ${amount}`,
    /**
     * The ledger's search: the field's accessible name, the words in it, and the control that empties it.
     *
     * The placeholder says what is searched rather than how, because the three fields it matches —
     * the description, the note and the contributor — are three different words on a row, and the user
     * is the one who knows which they mean. The clear control is named here because a lone cross says
     * nothing aloud.
     */
    searchLabel: "Tìm khoản trong quỹ",
    searchPlaceholder: "Tìm theo tên quán, nội dung, người nộp...",
    searchClear: "Xoá nội dung tìm kiếm",
    /** The three filters over the ledger. Each label carries the current search's own count, in parens. */
    filterLabel: "Lọc khoản theo loại",
    filterAll: (count: number): string => `Tất cả (${String(count)})`,
    filterIn: (count: number): string => `Thu (${String(count)})`,
    filterOut: (count: number): string => `Chi (${String(count)})`,
    /** The ⋯ menu on a row. It names the row it opens for; the glyph itself says nothing aloud. */
    rowMenu: (name: string): string => `Thao tác cho ${name}`,
    /**
     * Nothing is left after the search and the filter together. The query is named, so the sentence
     * refers to what the user typed rather than to the fund; the way out clears both, not one.
     */
    noMatch: (query: string): string =>
      query === "" ? "Không có khoản nào trong mục này." : `Không có khoản nào khớp “${query}”.`,
    clearFilters: "Xoá lọc và tìm kiếm",
    membersHeading: "Mỗi người đã góp",
    /**
     * The manage-members dialog, opened from the icon button beside the contributor select.
     *
     * The button's glyph says nothing aloud, so this is both its accessible name and the dialog's title.
     * Inside, each row's two glyph controls are named per member (`memberEditLabel`/`memberDeleteLabel`),
     * so a screen reader hears which person a control acts on rather than "button, button".
     */
    manageMembers: "Quản lý thành viên",
    manageMembersClose: "Đóng",
    /**
     * Adding a person, now from the dialog's own add row rather than an inline one on the sheet.
     *
     * `addPersonConfirm` is the visible verb on that row's confirm — the `+` is part of the label, not a
     * glyph — and the input's label is `memberNameLabel`.
     */
    addPersonConfirm: "+ Thêm",
    memberNameLabel: "Tên người góp",
    memberEditLabel: (name: string): string => `Đổi tên ${name}`,
    memberDeleteLabel: (name: string): string => `Xóa ${name}`,
    memberSaveLabel: (name: string): string => `Lưu tên ${name}`,
    /**
     * The delete question. It asks; it does not decide. Whether a member has a transaction is the
     * server's fact, so the API's own reason is what answers when the member cannot go.
     */
    memberDeleteQuestion: (name: string): string => `Bạn có chắc chắn muốn xóa thành viên ${name}?`,
    memberDeleteConfirm: "Xóa",
    /** The screen's own tab row, and the label that names it to a screen reader. */
    tabsLabel: "Xem",
    /** The record sheet. Its title names what it is doing and where. */
    sheetNewTitle: "Ghi một khoản",
    sheetEditTitle: "Sửa một khoản",
    /** The two choices the record sheet opens with, each a whole way to record a row. */
    modeExpense: "Chi tiêu",
    modeContribution: "Thu quỹ",
    fieldDescription: "Mô tả",
    fieldParticipant: "Người góp",
    /** The contributor select before anyone exists: a state, not a money slot, so it stays words. */
    noParticipant: "Chưa có người góp",
    fieldAmount: "Số tiền (đồng)",
    /**
     * The suggestion row under the amount field. Its label names what the chips are — the amount carried
     * up a place, derived from what was typed — never a fact about the trip.
     */
    amountSuggestions: "Số tiền gợi ý",
    /** The date this entry belongs to. Defaulted to today and free to be any day — before the trip included. */
    fieldDay: "Ngày (không bắt buộc)",
    /*
     * What the chosen date becomes, said under the control.
     *
     * Inside the trip it is that day, named by `dayLabel` exactly as the ledger's own day head names it, so
     * the sheet and the list cannot describe one day two ways. A date before the start or after the end is
     * not a day of this trip: money can move before a trip, so the line says which side it falls on in words
     * rather than leaving the day silently unset — the user can see the app understood it, not that it
     * mapped the row to day one.
     */
    dayBeforeTrip: "Trước chuyến đi — không thuộc ngày nào của chuyến.",
    dayAfterTrip: "Sau chuyến đi — không thuộc ngày nào của chuyến.",
    fieldNote: "Ghi chú (không bắt buộc)",
    fieldNotePlaceholder: "Nhập ghi chú (không bắt buộc)...",
    cancel: "Huỷ",
    save: "Ghi",
    /** The record button once an amount is typed: the figure it will write, in the button's own label. */
    saveWithAmount: (amount: string): string => `Ghi (+${amount})`,
    saveEdit: "Lưu",
    /** Row actions. The marker a correction leaves is neutral, not a warning. */
    edit: "Sửa",
    remove: "Xoá",
    confirmRemove: "Xoá?",
    fresh: "vừa ghi",
    edited: "đã sửa",
    /** What the form refuses, said beside the field rather than as a generic error. */
    needDescription: "Chưa có mô tả cho khoản chi.",
    needAmount: "Số tiền phải là số nguyên đồng lớn hơn 0.",
    needParticipant: "Chọn người đã góp.",
    needName: "Nhập tên người góp.",
  },

  /**
   * The planner's option screen. The figures themselves are never copy — they are rendered from the
   * plan variant the API stored, with `copy.state.unknown` wherever the routing provider did not
   * answer. Only the framing sentences live here.
   */
  planner: {
    /*
     * The rail's step names, and why there are four.
     *
     * Named for what the user does in each rather than for what the code builds, and short enough that all
     * of them share a phone's width. The constraint step (S09) was removed rather than redesigned: measured,
     * `wake_time` and `check_in_time` only shifted clock times after selection and order were already fixed,
     * `max_driving_minutes` only appended a warning and never trimmed, reordered or dropped a stop, and under
     * a user arrangement all three were inert because the engine bypasses the arrangement entirely. The
     * published request fields, their columns and their defaults stay — removing a field from a published
     * request is a breaking change, and the plan handler still reads `wake_time`, `check_in_time` and
     * `max_driving_minutes` from the trip's own row. Only the interface stops offering them.
     */
    steps: ["Cài đặt", "Xếp chỗ", "Xem lại & chốt", "Lộ trình"],
    /*
     * Starting over, confirmed.
     *
     * "Đặt lại" throws the whole draft away — dates, party, lodging, arrangement, time slots and any plan
     * already generated — and it did so on one tap, with nothing but a `title` attribute to warn. A `title`
     * is read by a mouse hovering over the chip and by nobody else: not by a thumb, not by a keyboard, and
     * it disappears before the tap that follows it. The confirm is the app's own dialog — the one the trips
     * list deletes with — because discarding a draft and deleting a trip are the same class of act.
     *
     * It names the trip, which is the one fact that says *which* draft is about to go: a second one may be
     * open in another tab, and "the draft" names nothing the user can check. `confirmResetUnnamed` covers
     * the state before an area is chosen, where the trip has no name yet and "this draft" is the only true
     * description of it — the reset chip is on screen from step 2, and step 2 can be reached with the area
     * cleared again from step 1.
     */
    confirmReset: (title: string): string => `Đặt lại "${title}"?`,
    confirmResetUnnamed: "Đặt lại bản nháp này?",
    /**
     * What goes, listed rather than summarised.
     *
     * "The draft" is exactly the word a user cannot weigh; the parts of it are things they remember
     * choosing. The last sentence is the one reassurance the reset can honestly give, and it is the same
     * one the trip-delete dialog gives: the places are the user's, and nothing here touches them.
     */
    resetWarns:
      "Các ngày, thành viên, chỗ ở, cách xếp địa điểm, khung giờ và lộ trình đã tạo đều bị xoá, và không hoàn tác được. Những địa điểm bạn đã lưu vẫn giữ nguyên.",
    /*
     * The roadmap step with no chosen plan to draw.
     *
     * A state the screen must never reach silently: it did, through a button that wrote step 4 while the
     * plan was still being generated, and what the user saw was a page with no content and nothing to read.
     */
    nothingToDraw: "Chưa có lịch trình nào để xem. Hãy tạo lộ trình rồi chốt.",
    /**
     * Back to the plan, from the roadmap's empty state.
     *
     * "Về ba phương án" named a screen that no longer exists: step 4 is one review card, with the strategy
     * chips above it only where there is no arrangement to decide the route. It is the empty state's only
     * content and its way out: the roadmap's own pinned bar used to carry a second copy, going to step 4
     * exactly as the head's "Quay lại" does, and that duplication is gone — so this string now names one
     * control, the recovery button on a screen that otherwise has nothing to read.
     */
    backToPlan: "Về kế hoạch",
    /*
     * The arrange step's suggestion: two numbers and the action that removes the difference.
     *
     * The two numbers and nothing else, because the step's own help line now states the rule (an untimed day
     * is ordered for the shortest drive, a timed one is driven as arranged) and repeating it here would be the
     * same sentence twice on one screen.
     */
    reorderNumbers: (shortest: string, arranged: string): string =>
      `Thứ tự ngắn nhất đi ${shortest}; thứ tự bạn chạm sẽ đi ${arranged}.`,
    reorderAction: "Xếp lại theo thứ tự ngắn nhất",
    /*
     * The way back, in the same box the suggestion came from.
     *
     * The owner asked for this to be able to compare the two orders: take the suggestion, see the day at the
     * shorter distance, and put the arrangement back the way they had it. So the sentence says what was done
     * — the box is no longer offering anything, it is reporting — and the button names what it restores
     * rather than saying "Hoàn tác" alone, because the undo is available on one day at a time and on one step
     * of that day's history.
     */
    reorderUndoNote: "Đã xếp lại theo thứ tự ngắn nhất.",
    reorderUndoAction: "Hoàn tác về thứ tự bạn xếp",
    /** AC-6.1: routing unreachable is a stated fact, not a silent blank where a distance would go. */
    routingUnavailable:
      "Không đo được khoảng cách lái xe (không truy cập được nhà cung cấp bản đồ). Các điểm dừng vẫn được hiển thị, chỉ thiếu số liệu đường đi.",
    /**
     * One leg of the arranged day, between two of its rows.
     *
     * Distance first, and in the panel's own words: the line under the map says "45,9 km đường đi · 1 giờ 12
     * phút lái xe" and a row that phrased the same measurement differently would read as a second fact. The
     * numbers arrive already formatted — `formatKm` and `formatDriving` own "chưa xác định" for a leg the
     * provider did not price — so this only writes them down.
     */
    legDrive: (distance: string, driving: string): string => `${distance} đường đi · ${driving} lái xe`,
    /** AC-4.5: a constraint the plan could not satisfy is named, so the user can decide to relax it. */
    unsatisfied: "Phương án này không đảm bảo được:",
    /*
     * The review step: the plans, read and then committed to.
     *
     * The screen used to compare three variants and the owner cut that: measured, with an arrangement all
     * three carried character-identical day chains and the same 12 điểm / 88.2 km / 111 phút, differing
     * only in rest hours. Asking a user to choose between three things that are not different is not a
     * choice, so with an arrangement the screen shows the one plan their own pace asked for. Without one
     * the variants really do differ, so the chips above the card offer the strategy and this card follows
     * whichever is picked — see `strategyLabel`.
     *
     * The button's verb is "chốt" and not "chọn", because selecting a plan is a server write: it is what
     * moves the trip from `draft` to `planned`, which the trips list then reports. "Chọn" would name a
     * screen state; "chốt lộ trình này" names the itinerary being committed to, which is what the step is
     * for now that it is the one that finalises the trip.
     */
    reviewHeading: "Xem lại lộ trình",
    matchesPace: "Đúng nhịp bạn đã chọn",
    confirmPlan: "Chốt lộ trình này & Lưu",
    confirmingPlan: "Đang lưu…",
    /*
     * The arranging step's forward action, and it names the act.
     *
     * It used to be the plain forward word ("Tiếp"), which named the destination the rail already names and
     * left the act unsaid: pressing it only saved the arrangement, and the review step arrived with a
     * generate button of its own. Generation is the act now — the arrangement is the instruction, and this
     * button is the moment the user asks for the itinerary built from it — so the label says what pressing
     * it does. The review step no longer offers generation at all: arriving there is what asks for a plan,
     * and `generatingPlan` is the one line it shows while that is happening.
     */
    createPlan: "Tạo lộ trình",
    /** The review step's line while the plans it was asked for are being built. */
    generatingPlan: "Đang tạo lộ trình…",
    /*
     * The strategy choice on the review step, shown only when there is no arrangement.
     *
     * With an arrangement all three strategies produce the same route and day split, so there is nothing to
     * choose; without one they differ, and this names the group of chips that picks between them.
     */
    strategyLabel: "Chọn nhịp độ lộ trình",
    /*
     * The one way back, in the sticky head rather than in a step's pinned bar.
     *
     * The two labels are the two things the control actually does: from any step after the first it takes
     * one step back, and on the first it leaves the planner altogether (history when it was opened from
     * one, the trip list otherwise — so it says "Thoát", not a destination). A pinned-bar copy of it was
     * the owner's report: two "Quay lại" on one screen, and on the constraints step the lower one was
     * wired to the step it already sat on and did nothing. There is one back control, and this is it.
     */
    backShort: "Quay lại",
    exit: "Thoát",
    /**
     * The empty-day count, on its own line under the arranging step's action.
     *
     * A warning rather than a status: leaving a day empty is allowed, and the step measures it rather than
     * preventing it. It is a sentence and not the bare chip ("3 ngày trống") because it is read here, at the
     * bottom of the screen, with nothing around it to say what a "ngày trống" is.
     */
    emptyDaysNote: (count: number): string => `Còn ${count} ngày trống chưa xếp địa điểm nào.`,
    /*
     * The bulk day split, offered only while the days are empty.
     *
     * It spreads every selected place over the trip's days as a starting point. The button is not rendered
     * once anything is arranged: the re-split that replaced an existing arrangement rebuilt the days from
     * `selectedPlaces` alone, so a stay and a place filed under another area — both on a day before — were
     * dropped, and the confirm it raised named only the arrangement being replaced. The action is the loss,
     * so the action is gone; a fresh trip has nothing to replace and this press stays.
     */
    arrangeSplit: "✨ Chia ngày",
  },

  /**
   * The date-range calendar's own words.
   *
   * Sits beside `planner` because the only thing it names is a trip's dates. The words are here rather
   * than in the component for the same reason every other string is: the grid prints six weeks of day
   * labels, and a "12 tháng 10 năm 2026" typed inside a component would be a second date vocabulary the
   * day anything else prints a date.
   *
   * `weekdays` is Monday-first. A Vietnamese week starts on Monday, which is also the order this app's
   * day chips are read in, and a Sunday-first header would silently move every date one column left.
   */
  calendar: {
    /** Names the trigger, and what the trigger's own value is read after. */
    triggerName: (label: string, value: string): string => `${label}: ${value}`,
    /** The dialog's accessible name, which is what a screen reader announces when the grid opens. */
    dialog: "Chọn khoảng ngày",
    /** The trigger with nothing chosen: a prompt rather than a blank, because a blank says nothing. */
    empty: "Chọn ngày",
    weekdays: ["T2", "T3", "T4", "T5", "T6", "T7", "CN"],
    month: (month: number, year: number): string => `Tháng ${month} năm ${year}`,
    /** The full date a day button announces: a bare "12" is the cell, not a date. */
    dayLabel: (day: number, month: number, year: number): string => `${day} tháng ${month} năm ${year}`,
    /** `12/10`. Zero-padded, so a pair of dates reads as two dates and not as two numbers. */
    shortDay: (day: number, month: number): string =>
      `${String(day).padStart(2, "0")}/${String(month).padStart(2, "0")}`,
    range: (start: string, end: string): string => `${start} → ${end}`,
    /** A start with no end yet, so the half-made choice is visible and not only internal state. */
    halfRange: (start: string): string => `${start} → …`,
    previousMonth: "Tháng trước",
    nextMonth: "Tháng sau",
  },

  /**
   * The place card's map.
   *
   * `clusterHint` and `hiddenCount` exist because a map that silently draws 40 pins on top of each
   * other looks broken. Saying how many are grouped is the difference between "this is a cluster" and
   * "this app lost my places".
   */
  placeMap: {
    emptyMap: "Chưa có địa điểm nào có toạ độ, nên chưa vẽ được bản đồ.",
    clusterHint: "Chạm vào một cụm để xem các địa điểm bên trong.",
    hiddenCount: (count: number): string => `và ${count} địa điểm khác`,
  },

  /**
   * The tab labels, in the order the shell renders them.
   *
   * Four, and the set is closed: a tab is a permanent claim on the strip of screen a thumb reaches
   * most easily, and the spec's fifth tab ("Khám phá") stays hidden until there is public content
   * behind it. `capture` is here as a *route* label rather than a tab — the capture form is an
   * overlay, because a user decides to save a place when they see it, not when browsing a list.
   */
  nav: {
    home: "Trang chủ",
    /**
     * The places tab.
     *
     * A tab label is a destination and has to stay short enough for four columns on a 320px phone,
     * which is the constraint that owns this word; `places.heading` names the same screen at more
     * length because a heading has room a tab does not.
     */
    places: "Địa điểm",
    trips: "Chuyến đi",
    profile: "Tài khoản",
    settings: "Cài đặt",
  },

  settings: {
    heading: "Cài đặt",
    intro: "Tùy chỉnh trải nghiệm và quản lý tài khoản",
    themeTitle: "Giao diện",
    themeSubtitle: "Chọn phong cách hiển thị phù hợp với môi trường của bạn.",
    themeLight: "Sáng ☀️",
    themeDark: "Tối 🌙",
    themeSystem: "Tự động ⚙️",
    accountTitle: "Tài khoản & Bảo mật",
    geminiTitle: "Tích hợp AI (Google Gemini)",
    /**
     * What the feature actually does.
     *
     * The first version promised "toạ độ GPS chuẩn xác", and the feature deliberately does not produce
     * coordinates from the model at all — a coordinate comes only from a Google Maps link the model
     * cites. The sentence says where a position does come from, so the promise matches the code.
     */
    geminiSubtitle: "Tra tên và địa chỉ địa điểm bằng Google Search. Toạ độ chỉ lấy từ link Google Maps chính thức.",
    geminiKeyLabel: "Gemini API Key (BYOK)",
    geminiKeyPlaceholder: "Dán khóa AIzaSy... tại đây",
    /**
     * Two claims in the first version were not true and are gone: a fixed "1,500 lượt/ngày" free
     * allowance (it depends on the account and changes), and that the key is stored "an toàn" — it is
     * in `localStorage`, which is browser storage, not secure storage.
     */
    geminiKeyHelp: "Khóa được lưu trong trình duyệt này và gửi tới máy chủ khi bạn dùng tính năng AI. Hạn mức miễn phí do tài khoản Google AI Studio của bạn quyết định.",
    geminiKeySaved: "Đã lưu Gemini API Key thành công!",
    geminiKeyCleared: "Đã xóa Gemini API Key.",
    geminiKeySave: "Lưu khóa",
    geminiKeyClear: "Xóa khóa",
    geminiGetFreeKey: "Lấy khóa miễn phí tại Google AI Studio ↗",
    aboutTitle: "Thông tin ứng dụng",
    versionLabel: "Phiên bản",
    versionValue: "v0.2.0 (Cartoon Edition)",
    aboutDesc: "Ứng dụng sắp xếp và tối ưu hóa lịch trình du lịch thông minh.",
  },

  profile: {
    heading: "Tài khoản",
    guest: "Khách",
    signOut: "Đăng xuất",
    sessions: "Thiết bị đã đăng nhập",
    thisDevice: "Thiết bị này",
    signOutDevice: "Đăng xuất thiết bị",
    /** When a session was last used. A bare timestamp leaves the reader to guess what it dates. */
    sessionUpdated: (when: string): string => `Cập nhật ${when}`,
    /**
     * Shown instead of sign-out while the deployment is in public mode.
     *
     * The screen's other affordances would lie here: there is no session to end, and "signed-in devices"
     * is not a thing a shared account has. This says what is actually true — one account, everyone sees
     * the same data, nobody has to sign in — so the account screen explains itself rather than showing
     * controls that would do nothing.
     */
    sharedNote:
      "Chế độ công khai đang bật: mọi người dùng chung một tài khoản và thấy cùng dữ liệu, không cần đăng nhập.",
    themeTitle: "Giao diện hoạt hình",
    themeLight: "Sáng ☀️",
    themeDark: "Tối 🌙",
    themeSystem: "Tự động ⚙️",
  },

  theme: {
    toggle: "Chuyển giao diện",
    light: "Sáng",
    dark: "Tối",
    switchDark: "Chuyển sang giao diện tối",
    switchLight: "Chuyển sang giao diện sáng",
  },

  /**
   * Messages for the failures a user can actually hit.
   *
   * Keyed by the API's problem type rather than by status, because the type is what the server
   * promises is stable — a slug can be served with a different status in a later version, and
   * `/api/v1` says the slug is the part that does not move.
   *
   * Deliberately NOT a copy of the server's `detail`. The server's message explains what the server
   * saw; these say what the person should do next, which is a different sentence.
   */
  problem: {
    unauthenticated: "Phiên đã hết hạn. Vui lòng đăng nhập lại.",
    forbidden: "Bạn không có quyền làm việc này.",
    "not-found": "Không tìm thấy.",
    conflict: "Dữ liệu này đã tồn tại.",
    "constraints-unsatisfiable": "Không thể thoả mãn mọi ràng buộc đã chọn.",
    "quota-exceeded": "Tạm thời đã chạm giới hạn. Vui lòng thử lại sau.",
    "provider-unavailable": "Dịch vụ bên ngoài đang tạm lỗi. Bạn vẫn dùng được phần đã lưu.",
    "not-implemented": "Tính năng này chưa có trong bản này.",
    network: "Không kết nối được máy chủ. Kiểm tra mạng rồi thử lại.",
    "invalid-response": "Máy chủ trả về dữ liệu không mong đợi.",
    /** The fallback. Shown when nothing more specific is known, which is rare by construction. */
    unknown: "Có lỗi xảy ra. Vui lòng thử lại.",
  },

  /** The saved-places list, story 2.9b. */
  places: {
    heading: "Địa điểm đã lưu",
    edit: "Sửa",
    remove: "Xoá",
    /**
     * A delete is confirmed with the place's own name, so the user can see what they are about to lose.
     * `affected_itinerary_items > 0` adds the consequence, because a place inside a planned trip is a
     * different decision from one that is not.
     */
    confirmRemove: (name: string): string => `Xoá “${name}”?`,
    confirmRemoveInTrips: (name: string, trips: number): string =>
      `Xoá “${name}”? Địa điểm này đang nằm trong ${trips} mục lịch trình.`,
    removed: "Đã xoá.",
    orphanedAreas: (names: string): string => `Khu vực không còn địa điểm nào: ${names}.`,
    searchLabel: "Tìm trong phần đã lưu",
    searchPlaceholder: "Theo tên, địa chỉ, khu vực hoặc ghi chú",
    searchAction: "Tìm",
    searchingFor: (text: string): string => `Đang lọc theo “${text}”.`,
    /** The card's second line when a place has neither an address nor an area. */
    noAddress: "chưa có địa chỉ",
    /** The mark beside the address of a place that cannot be routed to. */
    notLocated: "chưa có toạ độ",
    /*
     * The three ways to read the list, and why there are three.
     *
     * "Khu vực" is the default because a trip is planned by area — "what have I got in Đà Lạt" is the
     * question behind most visits to this screen. "Loại hình" answers the other one a user arrives with
     * ("where was that café"), and flat is for hunting one specific row, when a heading between rows is
     * just something to scroll past.
     */
    viewByArea: "Theo khu vực",
    viewByType: "Theo loại hình",
    viewAll: "Tất cả",
    viewLabel: "Cách gom nhóm",
    /** The expand/collapse control on a section header, named for the section it belongs to. */
    collapseSection: (name: string): string => `Thu gọn ${name}`,
    expandSection: (name: string): string => `Mở ${name}`,
    sectionCount: (count: number): string => `${count} địa điểm`,
    clearSearch: "Bỏ lọc",
    noCoordinates: "chưa có toạ độ",
    /**
     * A place saved without a category. A real state rather than a gap: the capture form does not
     * require one, so the row says so instead of leaving the glyph column empty.
     */
    noCategory: "chưa phân loại",
    emptyNoPlaces: "Bạn chưa lưu địa điểm nào. Tìm một nơi và lưu lại để bắt đầu.",
    emptyNoMatch: "Không địa điểm nào khớp từ bạn vừa nhập. Từ khoá tìm cả địa chỉ, khu vực và ghi chú.",

    /*
     * The filters.
     *
     * Three, and each one is a question the list could not answer before: "which of these are cafés",
     * "which are in Đà Lạt", and "which still have no coordinate". The last is the one that matters
     * most — a place without a coordinate cannot enter a route, and the only route to fixing a plan
     * that quietly lost a stop was scrolling the whole list looking for a small flag.
     */
    filtersLabel: "Lọc",
    filterCategory: "Loại",
    filterArea: "Khu vực",
    filterAll: "Tất cả",
    filterNoLocation: "Chưa có toạ độ",
    filtersClear: "Bỏ hết lọc",
    filteredCount: (shown: number): string => `${shown} địa điểm khớp bộ lọc.`,
    emptyNoMatchFiltered:
      "Không địa điểm nào khớp bộ lọc này. Bỏ bớt một điều kiện để xem thêm.",
  },

  /** The provider search screen, story 2.4. */
} as const;

export type Copy = typeof copy;

/**
 * The place categories, with the label and the glyph each one shows.
 *
 * Lives here rather than in the capture form, because two screens need it now and had begun to diverge:
 * `Capture` carried the list to render its chips, and the place list needs a glyph per row. A second
 * copy is how "Cà phê" in the form and "cafe" in the list become two different vocabularies, which is
 * the same failure the design tokens exist to prevent — one definition, read by everything that shows
 * it.
 *
 * The glyph is not decoration. It is the row's leading visual, and the app has no photographs: a
 * user-authored capture (ADR-004) stores a reference, never a fetched image, so a list of places is
 * otherwise a wall of text with nothing to scan by. An emoji costs no request, no storage and no
 * rights question, and it is drawn from a font that ships with the device.
 *
 * `restaurant`, `attraction`, `market`, `transport` and `other` are in the enum because the contract
 * allows them and the capture form offers them; every value gets a glyph so no row can render blank.
 */
export const categoryChoices: ReadonlyArray<{ value: PlaceCategory; label: string; glyph: string }> = [
  { value: "cafe", label: "Cà phê", glyph: "☕" },
  { value: "restaurant", label: "Quán ăn", glyph: "🍜" },
  { value: "dessert", label: "Tráng miệng", glyph: "🍰" },
  { value: "attraction", label: "Tham quan", glyph: "📷" },
  { value: "nature", label: "Thiên nhiên", glyph: "🌲" },
  { value: "stay", label: "Lưu trú", glyph: "🛏️" },
  { value: "market", label: "Chợ", glyph: "🧺" },
  { value: "shopping", label: "Mua sắm", glyph: "🛍️" },
  { value: "transport", label: "Di chuyển", glyph: "🚌" },
  { value: "other", label: "Khác", glyph: "📍" },
];

/** One category's label, for a row that prints it. */
const CATEGORY_BY_VALUE = new Map(categoryChoices.map((choice) => [choice.value, choice]));

/**
 * The glyph for a place's categories, and the label of the one it came from.
 *
 * A place may carry several categories and the row shows one square, so the first is the one that
 * counts: `categories` is stored in the order the user chose them, which makes the first their primary
 * intent rather than an arbitrary pick.
 *
 * A place with no categories gets the same pin as `other` rather than nothing at all, so a row is never
 * missing its leading visual — the flag is a real state, "not classified yet", and a hole in the list
 * reads as a rendering bug instead.
 */
/**
 * The Vietnamese label and glyph for one category.
 *
 * The list prints a place's *own* categories, so it needs the lookup per value and not only for the
 * first one — and the row used to print the raw slugs, which is how `dessert` and `nature` reached the
 * screen in English beside Vietnamese everything-else. `CATEGORY_BY_VALUE` is the same map the pickers
 * use, so a category renamed in one place renames in all of them.
 */
export function categoryOf(value: PlaceCategory): { glyph: string; label: string } {
  return CATEGORY_BY_VALUE.get(value) ?? { glyph: "📍", label: copy.places.noCategory };
}

export function categoryGlyph(categories: readonly PlaceCategory[]): { glyph: string; label: string } {
  const first = categories[0];
  const choice = first === undefined ? undefined : CATEGORY_BY_VALUE.get(first);
  return choice ?? { glyph: "📍", label: copy.places.noCategory };
}
